// #294 redesign (CP 294 Section 2) — one tab per browser plays the alert sounds (the critical sound and
// the chime). Only a tab whose sound is unlocked may be that tab: it holds the Web Lock
// "detercam-alert-sound" while its sound runs, and lets go when the sound stops running (the browser
// suspended its AudioContext: background tab, OS sleep — Review Focus 1), when the tab closes (the browser
// frees the lock), or on stop (logout, unmount), so another unlocked tab takes over at once. Where the
// browser has no Web Locks, or they fail, every tab leads (every tab plays, as before the redesign).
//
// Shared between the tabs: the quiet time ("Quiet 2 min" quiets every tab), the time of the last play
// (a new leader keeps the rhythm and never plays on top of the old one) and the newest sounded event id (a
// new leader never plays an alert again that another tab sounded), over a BroadcastChannel, else the
// storage event; a stored copy lets a tab opened later read them. Where storage is blocked and there is no
// BroadcastChannel they stay per tab. A logout is announced to the other tabs (Review Focus 4).
// #328 (owner ruling b) — a tab that announces an event asks every tab for the chime with the event id: the
// leader plays it once per event, even when its own WebSocket is the dead one.
//
// No React here; every browser call is in try/catch. createSoundShare takes its browser pieces as
// arguments (tests give it fakes); the module-level functions use one share made from the globals the
// first time one is called.

import type { ChimeKind } from '@/types'

const LOCK_NAME = 'detercam-alert-sound'
const CHANNEL_NAME = 'detercam-alert-sound'
const STORAGE_KEY = 'detercam-alert-sound'
const LOGOUT_KEY = 'detercam-alert-sound-logout'
const SEEN_KEY = 'detercam-alert-sound-seen'
const CHIME_KEY = 'detercam-alert-sound-chime'

export interface QuietState {
  /** Quiet until this time (ms, this computer's clock); 0 = not quiet. */
  until: number
  /** When it was set or cleared: a later change wins between tabs. */
  setAt: number
}

export interface LeadershipOptions {
  /** Whether this tab's sound runs (isAlertSoundReady). */
  isUnlocked: () => boolean
  /** Called whenever that may have changed (subscribeAlertSound): leadership is checked again. */
  subscribe: (listener: () => void) => () => void
  onLeaderChange?: (leader: boolean) => void
}

export interface SoundShareDeps {
  locks?: LockManager
  Channel?: typeof BroadcastChannel
  storage?: Storage
  now?: () => number
}

export interface SoundShare {
  /** Lead while the sound is unlocked; returns the stop (unmount). One leadership per share at a time. */
  startLeadership(opts: LeadershipOptions): () => void
  /** Give the lock up now (logout). */
  stopLeadership(): void
  isSoundLeader(): boolean
  getQuiet(): QuietState
  setQuiet(untilMs: number): void
  /** A new alert arrived at `arrivalMs`: end the quiet time, unless it was set after that arrival (a tab
   *  that sees an old alert late never wipes a fresh quiet). */
  clearQuietIfOlderThan(arrivalMs: number): void
  getLastPlayed(): number
  setLastPlayed(ms: number): void
  /** The newest critical event id a tab has sounded (ids only grow): a catch-up play is covered when
   *  every open alert inside its window is at or below it — no clocks involved. */
  getLastSoundedId(): number
  setLastSoundedId(id: number): void
  /** Called when a shared value or this tab's leadership changes. */
  subscribeShared(cb: () => void): () => void
  /** Tell the other tabs of this browser that the user logged out. */
  announceLogout(): void
  /** Called when another tab announced a logout. */
  onLogoutAnnounced(cb: () => void): () => void
  /** Tell the other tabs that this tab received a critical notification (the leader's own WebSocket may
   *  be down: it refreshes its open list at once instead of waiting for its next poll). */
  announceCriticalSeen(): void
  /** Called when another tab announced a critical notification. */
  onCriticalSeen(cb: () => void): () => void
  /** #328 — this tab announced an event: ask for its chime. Heard by this tab at once and by the other tabs
   *  (the sound leader plays it once per event id). */
  requestChime(eventId: number, kind?: ChimeKind): void
  /** #328 — called with the event id of every chime request, from this tab or another (#413: and its kind). */
  onChimeRequested(cb: (eventId: number, kind: ChimeKind) => void): () => void
  /** Stop everything this share listens to (tests). */
  dispose(): void
}

type StateMessage = { type: 'state'; quiet: QuietState; lastPlayed: number; lastSoundedId: number }
type Message = StateMessage | { type: 'logout' } | { type: 'critical-seen' } | { type: 'chime'; id: number; kind?: 'suspicious' }

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

function readState(raw: unknown): { quiet?: QuietState; lastPlayed?: number; lastSoundedId?: number } {
  if (!raw || typeof raw !== 'object') return {}
  const r = raw as { quiet?: { until?: unknown; setAt?: unknown }; lastPlayed?: unknown; lastSoundedId?: unknown }
  const out: { quiet?: QuietState; lastPlayed?: number; lastSoundedId?: number } = {}
  if (r.quiet && typeof r.quiet === 'object' && isNum(r.quiet.until) && isNum(r.quiet.setAt)) {
    out.quiet = { until: r.quiet.until, setAt: r.quiet.setAt }
  }
  if (isNum(r.lastPlayed)) out.lastPlayed = r.lastPlayed
  if (isNum(r.lastSoundedId)) out.lastSoundedId = r.lastSoundedId
  return out
}

interface Session {
  opts: LeadershipOptions
  unsubscribe: () => void
  /** The lock request waiting to be granted (aborted when the sound stops or on stop). */
  pending: AbortController | null
  /** Resolving it gives the held lock back. */
  release: (() => void) | null
  /** The browser's Web Locks failed (not an abort): this tab leads as if there were none. */
  failedOpen: boolean
}

export function createSoundShare(deps: SoundShareDeps = {}): SoundShare {
  const { locks, storage } = deps
  const now = deps.now ?? (() => Date.now())

  let quiet: QuietState = { until: 0, setAt: 0 }
  let lastPlayed = 0
  let lastSoundedId = 0
  let leader = false
  let session: Session | null = null
  const sharedListeners = new Set<() => void>()
  const logoutListeners = new Set<() => void>()
  const seenListeners = new Set<() => void>()
  const chimeListeners = new Set<(eventId: number, kind: ChimeKind) => void>()

  const notify = () => sharedListeners.forEach((cb) => { try { cb() } catch { /* a listener never breaks the share */ } })

  /** Take in another tab's values: the later quiet change, the latest play. Returns whether anything changed. */
  function merge(raw: unknown): boolean {
    const got = readState(raw)
    let changed = false
    if (got.quiet && (got.quiet.setAt > quiet.setAt || (got.quiet.setAt === quiet.setAt && got.quiet.until > quiet.until))) {
      quiet = got.quiet
      changed = true
    }
    if (got.lastPlayed !== undefined && got.lastPlayed > lastPlayed) {
      lastPlayed = got.lastPlayed
      changed = true
    }
    if (got.lastSoundedId !== undefined && got.lastSoundedId > lastSoundedId) {
      lastSoundedId = got.lastSoundedId
      changed = true
    }
    return changed
  }

  // A tab opened later starts from the stored copy.
  try {
    const stored = storage?.getItem(STORAGE_KEY)
    if (stored) merge(JSON.parse(stored))
  } catch {
    // blocked or corrupted: start empty
  }

  let channel: BroadcastChannel | null = null
  try {
    if (deps.Channel) channel = new deps.Channel(CHANNEL_NAME)
  } catch {
    channel = null
  }

  function receive(msg: unknown) {
    if (!msg || typeof msg !== 'object') return
    const m = msg as Message
    if (m.type === 'logout') logoutListeners.forEach((cb) => { try { cb() } catch { /* keep going */ } })
    else if (m.type === 'critical-seen') seenListeners.forEach((cb) => { try { cb() } catch { /* keep going */ } })
    else if (m.type === 'chime' && isNum(m.id)) chimeListeners.forEach((cb) => { try { cb(m.id, m.kind === 'suspicious' ? 'suspicious' : 'regular') } catch { /* keep going */ } })
    else if (m.type === 'state' && merge(m)) notify()
  }
  if (channel) channel.onmessage = (e: MessageEvent) => receive(e.data)

  // Without a channel, the storage event (fired in the OTHER tabs of this browser) carries the values.
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      try { if (merge(JSON.parse(e.newValue))) notify() } catch { /* corrupted: ignore */ }
    } else if (e.key === LOGOUT_KEY && e.newValue) {
      receive({ type: 'logout' })
    } else if (e.key === SEEN_KEY && e.newValue) {
      receive({ type: 'critical-seen' })
    } else if (e.key === CHIME_KEY && e.newValue) {
      // "<id>:<stamp>" = regular; "<id>:s:<stamp>" = suspicious (#413; an older build still reads the id first)
      const parts = e.newValue.split(':')
      receive({ type: 'chime', id: Number(parts[0]), ...(parts[1] === 's' ? { kind: 'suspicious' as const } : {}) })
    }
  }
  const storageTarget = !channel && storage && typeof window !== 'undefined' ? window : null
  storageTarget?.addEventListener('storage', onStorage)

  /** A one-off message to the other tabs: the channel, else a storage key whose value changes every time
   *  (`data` first, when the message carries some). */
  function signal(msg: Message, key: string, data?: string) {
    if (channel) {
      try { channel.postMessage(msg) } catch { /* closed */ }
      return
    }
    const stamp = `${now()}-${Math.random()}`
    try { storage?.setItem(key, data === undefined ? stamp : `${data}:${stamp}`) } catch { /* blocked: this tab only */ }
  }

  function publish() {
    const state = { quiet, lastPlayed, lastSoundedId }
    try { storage?.setItem(STORAGE_KEY, JSON.stringify(state)) } catch { /* blocked: per tab */ }
    try { channel?.postMessage({ type: 'state', ...state } satisfies StateMessage) } catch { /* closed */ }
    notify()
  }

  // ---- leadership ----

  function setLeader(v: boolean) {
    if (leader === v) return
    leader = v
    try { session?.opts.onLeaderChange?.(v) } catch { /* keep going */ }
    notify()
  }

  function unlocked(s: Session): boolean {
    try { return s.opts.isUnlocked() === true } catch { return false }
  }

  function giveUp(s: Session) {
    if (s.pending) { s.pending.abort(); s.pending = null }
    if (s.release) { const release = s.release; s.release = null; release() }
  }

  function request(s: Session) {
    if (!locks) return
    const ac = new AbortController()
    s.pending = ac
    try {
      locks.request(LOCK_NAME, { signal: ac.signal }, () => {
        // A grant for a request this tab no longer wants (stopped, sound blocked since): give it back.
        if (session !== s || s.pending !== ac || !unlocked(s)) {
          if (s.pending === ac) s.pending = null
          return undefined
        }
        s.pending = null
        setLeader(true)
        return new Promise<void>((resolve) => {
          s.release = () => {
            resolve()
            setLeader(false)
          }
        })
      }).catch((err: unknown) => {
        if (s.pending === ac) s.pending = null
        // aborted while waiting (the sound got blocked, or stop): ask again on the next check
        if (ac.signal.aborted || (err as { name?: unknown } | null)?.name === 'AbortError') return
        failOpen(s)
      })
    } catch {
      s.pending = null
      failOpen(s)
    }
  }

  /** Web Locks failed: lead as without them (every such tab plays) — a double play beats silence. */
  function failOpen(s: Session) {
    if (session !== s) return
    s.failedOpen = true
    setLeader(true)
  }

  function check() {
    const s = session
    if (!s) return
    if (!locks || s.failedOpen) { setLeader(true); return }
    if (unlocked(s)) {
      if (!s.pending && !s.release) request(s)
    } else {
      giveUp(s)
    }
  }

  function stopLeadership() {
    const s = session
    if (!s) return
    try { s.unsubscribe() } catch { /* keep going */ }
    giveUp(s)            // the held lock goes back now; onLeaderChange(false) still reaches this session
    setLeader(false)     // without Web Locks this tab simply stops leading
    session = null
  }

  function startLeadership(opts: LeadershipOptions): () => void {
    stopLeadership()
    const s: Session = { opts, unsubscribe: () => {}, pending: null, release: null, failedOpen: false }
    session = s
    try { s.unsubscribe = opts.subscribe(check) } catch { /* no notifications: checked once */ }
    check()
    return () => { if (session === s) stopLeadership() }
  }

  return {
    startLeadership,
    stopLeadership,
    isSoundLeader: () => leader,
    getQuiet: () => quiet,
    setQuiet(untilMs) {
      quiet = { until: untilMs, setAt: now() }
      publish()
    },
    clearQuietIfOlderThan(arrivalMs) {
      const t = now()
      if (quiet.until > t && arrivalMs > quiet.setAt) {
        quiet = { until: 0, setAt: t }
        publish()
      }
    },
    getLastPlayed: () => lastPlayed,
    setLastPlayed(ms) {
      lastPlayed = ms
      publish()
    },
    getLastSoundedId: () => lastSoundedId,
    setLastSoundedId(id) {
      if (!isNum(id) || id <= lastSoundedId) return
      lastSoundedId = id
      publish()
    },
    subscribeShared(cb) {
      sharedListeners.add(cb)
      return () => { sharedListeners.delete(cb) }
    },
    announceLogout() {
      signal({ type: 'logout' }, LOGOUT_KEY)
    },
    onLogoutAnnounced(cb) {
      logoutListeners.add(cb)
      return () => { logoutListeners.delete(cb) }
    },
    announceCriticalSeen() {
      signal({ type: 'critical-seen' }, SEEN_KEY)
    },
    onCriticalSeen(cb) {
      seenListeners.add(cb)
      return () => { seenListeners.delete(cb) }
    },
    requestChime(eventId, kind) {
      if (!isNum(eventId)) return
      const msg: Message = kind === 'suspicious' ? { type: 'chime', id: eventId, kind } : { type: 'chime', id: eventId }
      receive(msg)                                                  // this tab (a channel never echoes to itself)
      signal(msg, CHIME_KEY, kind === 'suspicious' ? `${eventId}:s` : String(eventId))
    },
    onChimeRequested(cb) {
      chimeListeners.add(cb)
      return () => { chimeListeners.delete(cb) }
    },
    dispose() {
      stopLeadership()
      storageTarget?.removeEventListener('storage', onStorage)
      try { channel?.close() } catch { /* already closed */ }
      channel = null
      sharedListeners.clear()
      logoutListeners.clear()
      seenListeners.clear()
      chimeListeners.clear()
    },
  }
}

function fromGlobals(): SoundShareDeps {
  const deps: SoundShareDeps = {}
  try { deps.locks = typeof navigator !== 'undefined' && navigator.locks ? navigator.locks : undefined } catch { /* none */ }
  try { deps.Channel = typeof BroadcastChannel !== 'undefined' ? BroadcastChannel : undefined } catch { /* none */ }
  try { deps.storage = typeof window !== 'undefined' ? window.localStorage : undefined } catch { /* blocked */ }
  return deps
}

let shared: SoundShare | null = null
function share(): SoundShare {
  if (!shared) shared = createSoundShare(fromGlobals())
  return shared
}

export const startLeadership = (opts: LeadershipOptions): (() => void) => share().startLeadership(opts)
export const stopLeadership = (): void => share().stopLeadership()
export const isSoundLeader = (): boolean => share().isSoundLeader()
export const getQuiet = (): QuietState => share().getQuiet()
export const setQuiet = (untilMs: number): void => share().setQuiet(untilMs)
export const clearQuietIfOlderThan = (arrivalMs: number): void => share().clearQuietIfOlderThan(arrivalMs)
export const getLastPlayed = (): number => share().getLastPlayed()
export const setLastPlayed = (ms: number): void => share().setLastPlayed(ms)
export const getLastSoundedId = (): number => share().getLastSoundedId()
export const setLastSoundedId = (id: number): void => share().setLastSoundedId(id)
export const subscribeShared = (cb: () => void): (() => void) => share().subscribeShared(cb)
export const announceLogout = (): void => share().announceLogout()
export const onLogoutAnnounced = (cb: () => void): (() => void) => share().onLogoutAnnounced(cb)
export const announceCriticalSeen = (): void => share().announceCriticalSeen()
export const onCriticalSeen = (cb: () => void): (() => void) => share().onCriticalSeen(cb)
export const requestChime = (eventId: number, kind?: ChimeKind): void => share().requestChime(eventId, kind)
export const onChimeRequested = (cb: (eventId: number, kind: ChimeKind) => void): (() => void) => share().onChimeRequested(cb)

/** Tests only: use this share as the default (null = make a new one from the globals on next use). */
export function __setSoundShareForTests(s: SoundShare | null) {
  if (shared && shared !== s) shared.dispose()
  shared = s
}
